/**
 * Represents a query to be made.
 */

export class GVQuery {
  constructor() {
    this.origin = "";
    this.destination = "";
    // noinspection JSUnusedGlobalSymbols
    this.departureDate = new Date(Date.now());
    // noinspection JSUnusedGlobalSymbols
    this.returnDate = new Date(Date.now());
    this.roundtrip = true;
    this.adults = 1;
    this.children = 0;
    this.infants = 0;
    // noinspection JSUnusedGlobalSymbols
    this.alt_origin = null;
    // noinspection JSUnusedGlobalSymbols
    this.alt_destination = null;
    this.referrer_code = "6";
  }

  /**
   * Performs some minimal validation of the query.
   * @throws "gv_no_origin" If no origin has been set for the query.
   * @throws "gv_no_dest" If no destination has been set for the query.
   */
  validate() {
    if (!this.origin) {
      throw new Error("gv_no_origin");
    } else if (!this.destination) {
      throw new Error("gv_no_dest");
    }
  }

  createGaEventCode() {
    let code = [
      this._extractIata(this.origin),
      this._extractIata(this.destination),
    ];
    if (this.alt_origin && this.alt_destination) {
      code = code.concat(
        this._extractIata(this.alt_origin),
        this._extractIata(this.alt_destination)
      );
    }
    code = code.concat(this.departureDate.replace(/-/g, ""));
    if (this.roundtrip) {
      code = code.concat(this.returnDate.replace(/-/g, ""));
    }
    code = code.concat(this.adults, this.children, this.infants);
    return code.join("-");
  }

  _extractIata(location) {
    const re = [
      /^([a-z]{3}|nl)$/i,
      /[(\[]([a-z]{3}|nl)[)\]]/i,
      /^([a-z]{3}|nl)\W/i,
      /\W([a-z]{3}|nl)$/i,
      /\W([a-z]{3}|nl)\W/i,
    ];
    for (let i = 0; i < re.length; i++) {
      const match = re[i].exec(location);
      if (match) {
        return match[1];
      }
    }
    return null;
  }

  _formatSearchDate(date) {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0]
      .replace(/[-\/]/g, "");
  }
}

/**
 * Represents one of the flights in a result.
 */
export class GVFlightPart {
  /**
   * @param {String} flightNo The flight number
   * @param {number} duration The duration of this flight, in minutes
   * @param {String} airline The airline code for this flight
   * @param {Date} departureDate The date of departure
   * @param {Date} arrivalDate The date of arrival
   * @param {String} origin The IATA code for the origin of this flight
   * @param {String} destination The IATA code for the destination of this flight
   * @returns {GVFlightPart}
   */
  constructor(
    flightNo,
    duration,
    airline,
    departureDate,
    arrivalDate,
    origin,
    destination
  ) {
    // noinspection JSUnusedGlobalSymbols
    /**
     * The flight number for this flight part
     * @type {String}
     */
    this.flightNo = flightNo;
    /**
     * The duration this flight, in minutes
     * @type {number}
     */
    this.duration = duration;
    /**
     * The flight number
     * @type {string}
     */
    this.airline = airline;

    // noinspection JSUnusedGlobalSymbols
    /**
     * The date of departure
     * @type {Date}
     */
    this.departureDate = departureDate;

    // noinspection JSUnusedGlobalSymbols
    /**
     * The date of arrival
     * @type {Date}
     */
    this.arrivalDate = arrivalDate;

    /**
     * The IATA code for the origin of this flight
     * @type {String}
     */
    this.origin = origin;

    /**
     * The IATA code for the destination of this flight
     * @type {String}
     */
    this.destination = destination;
  }
}

/**
 * Represents a leg of a result.
 */
export class GVPartialResult {
  /**
   *
   * @param {GVFlightPart[]} parts
   * @param {number} duration
   */
  constructor(parts, duration) {
    /**
     *
     * @type {GVFlightPart[]}
     */
    this.parts = parts;

    /**
     * @type {number}
     */
    this.duration = duration;
  }

  /**
   * Gets the first flight in this leg.
   * @returns {GVFlightPart}
   */
  get firstPart() {
    return this.parts[0];
  }

  /**
   * Gets the last flight in this leg.
   * @returns {GVFlightPart}
   */
  get lastPart() {
    return this.parts[this.parts.length - 1];
  }

  /**
   * Gets the number of stops in this leg.
   * @returns {number}
   */
  get stops() {
    return this.parts.length - 1;
  }

  /**
   * Gets the moment of departure for this leg
   * @returns {Date}
   */
  get departureDate() {
    return this.firstPart.departureDate;
  }

  /**
   * Gets the moment of arrival for this leg
   * @returns {Date}
   */
  get arrivalDate() {
    return this.lastPart.arrivalDate;
  }

  /**
   * Gets the airport this leg departs from as an IATA code.
   * @returns {String}
   */
  get origin() {
    return this.firstPart.origin;
  }

  /**
   * Gets the airport this leg arrives at as an IATA code.
   * @returns {String}
   */
  get destination() {
    return this.lastPart.destination;
  }
}

/**
 * Represents an offer belonging to a result.
 */
export class GVOffer {
  constructor(code, url, price) {
    this.code = code;
    this.price = parseInt(price);
    this.url = url;
  }
}

/**
 * Represents a result from the engine
 */
export class GVResult {
  /**
   * @param jsonObj The JSON object from the result file.
   * @throws "gv_invalid_providers" If there is a mismatch between the amount of links and providers.
   */
  constructor(jsonObj) {
    const parseDate = function (dateString) {
      const year = parseInt(dateString.substring(0, 4), 10);
      // Javascript uses 0 indexed months, for some reason.
      const month = parseInt(dateString.substr(4, 2), 10) - 1;
      const day = parseInt(dateString.substr(6, 2), 10);
      const hour = parseInt(dateString.substr(8, 2), 10);
      const minute = parseInt(dateString.substr(10, 2), 10);

      // No one cares about seconds or miliseconds.
      return new Date(year, month, day, hour, minute, 0, 0);
    };

    /**
     * The number of adult passengers for which this result was obtained
     * @type {number}
     */
    this.adults = parseInt(jsonObj.a);

    /**
     * The number of child passengers for which this result was obtained
     * @type {number}
     */
    this.children = parseInt(jsonObj.c);

    /**
     * The number of infant passengers for which this result was obtained
     * @type {number}
     */
    this.infants = parseInt(jsonObj.i);

    /**
     * A string unique to this result.
     * @type {String}
     */
    this.unique = jsonObj.uniek;

    // Parse result providers.
    const urls = jsonObj.URL.split(" ");
    const aanbs = jsonObj.aanb.split("+");

    if (urls.length !== aanbs.length) {
      throw "gv_invalid_providers";
    }

    /**
     * Sale URLs with associated prices
     * @type {GVOffer[]}
     */
    this.offers = [];

    for (let i = 0; i < urls.length; i++) {
      const aanbSplit = aanbs[i].split("-");
      const code = aanbSplit[0];
      let price = parseInt(aanbSplit[1]);
      price = Math.round(price / (this.adults + this.children));
      this.offers.push(new GVOffer(code, urls[i], price));
    }

    jsonObj.links.forEach((elem) => {
      this.offers.push(new GVOffer(elem.code, elem.url, null));
    });

    // Parse result parts
    // First split in outbound/inbound.
    const uniekParts = jsonObj.uniek.split("+");
    const flightsNos = jsonObj.vlnr.split("+");
    const durations = jsonObj.vldrel.split("+");

    this.durations = jsonObj.vldrto.split("+");
    for (let i = 0; i < this.durations.length; i++) {
      this.durations[i] = +this.durations[i];
    }

    /**
     * The flights comprising this result
     * @type {GVFlightPart[][]}
     */
    this.flights = [];
    for (let i = 0; i < uniekParts.length; i++) {
      this.flights[i] = [];

      const unieks = uniekParts[i].split("-");
      const flightNoOneWay = flightsNos[i].split("-");
      const durationsOneWay = durations[i].split("-");
      for (let j = 0; j < unieks.length; j++) {
        const airline = unieks[j].substr(0, 2);
        const departureDate = parseDate(unieks[j].substr(2, 12));
        const arrivalDate = parseDate(unieks[j].substr(14, 12));
        const origin = unieks[j].substr(26, 3);
        const destination = unieks[j].substr(29, 3);
        this.flights[i].push(
          new GVFlightPart(
            flightNoOneWay[j],
            parseInt(durationsOneWay[j]),
            airline,
            departureDate,
            arrivalDate,
            origin,
            destination
          )
        );
      }
    }

    this.roundtrip = uniekParts.length > 1;

    this.outbound = new GVPartialResult(this.flights[0], this.durations[0]);

    if (this.roundtrip) {
      this.inbound = new GVPartialResult(this.flights[1], this.durations[1]);
    }
  }

  /**
   * Gets the duration of the longest leg of this result.
   * @returns {number}
   */
  get longestDuration() {
    return this.roundtrip
      ? Math.max(this.outbound.duration, this.inbound.duration)
      : this.outbound.duration;
  }

  /**
   * Gets the cheapest offer for this result.
   * @returns {GVOffer}
   */
  get cheapestOffer() {
    if (this._cheapestOffer == null) {
      var o = this.offers[0];
      for (var i of this.offers) {
        if (i.price < o.price) {
          o = i;
        }
      }
      this._cheapestOffer = o;
    }
    return this._cheapestOffer;
  }

  /**
   * Whether this is a result for multiple travelers
   */
  get multiplePersons() {
    return this.infants + this.adults + this.children !== 1;
  }

  /**
   * Gets the number of stops for the worst leg.
   * @returns {number}
   */
  get mostStops() {
    return this.roundtrip
      ? Math.max(this.outbound.stops, this.inbound.stops)
      : this.outbound.stops;
  }

  /**
   * Gets the price belonging to the cheapest offer for this result.
   * @returns {number}
   */
  get cheapestPrice() {
    return this.cheapestOffer.price;
  }

  /**
   * Gets the departure date for this result.
   * @returns {Date}
   */
  get departureDate() {
    return this.outbound.departureDate;
  }

  /**
   * Gets the arrival date for this result.
   * @returns {Date}
   */
  get returnDate() {
    return this.roundtrip ? this.inbound.arrivalDate : null;
  }

  /**
   * Gets the airport this result departs from, as an IATA code.
   * @returns {String}
   */
  get origin() {
    return this.outbound.origin;
  }

  /**
   * Gets the airport this result flies to, as an IATA code.
   * @returns {String}
   */
  get destination() {
    return this.outbound.destination;
  }

  /**
   * Gets the airlines operating this result.
   * @returns {Set<string>} A set of the airline codes found.
   */
  get airlines() {
    let airlines = new Set();
    for (let leg of this.flights)
      for (let flight of leg) {
        airlines.add(flight.airline);
      }
    return airlines;
  }

  /**
   * Compare function for sorting by price
   * @param {GVResult} a
   * @param {GVResult} b
   */
  static comparePrice(a, b) {
    return a.cheapestPrice - b.cheapestPrice;
  }

  /**
   * Compare function for sorting by number of stops
   * @param {GVResult} a
   * @param {GVResult} b
   */
  static compareStops(a, b) {
    let c = a.mostStops - b.mostStops;
    return c || GVResult.comparePrice(a, b);
  }

  /**
   * Compare function for sorting by price
   * @param {GVResult} a
   * @param {GVResult} b
   */
  static compareDuration(a, b) {
    const c = a.longestDuration - b.longestDuration;
    return c || GVResult.comparePrice(a, b);
  }
}
